import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getExtendedCrewListFromVessel } from "../../../api/crew/crew";
import { VesselContext } from "../../../routes/vessel/VesselRoot";
import wordUtils from "../../../utils/wordUtils";
import { Button } from "../../button/Button";
import Col from "../../grid/Col";
import Row from "../../grid/Row";
import { Word } from "../../lang/Word";
import { ItemList } from "../../list/ItemList";
import Image from "../../media/Image";
import { TitleDescriptionText } from "../../typography/TitleDescriptionText";
import { UserSelect } from "../UserSelect";
import { Checkbox } from "../../form/Checkbox";
import { Text } from "../../typography/Text";

const msgKey = "crewSelect";

export const CrewMultiSelect = ({ expanded, selectedList = [], excludeUserIdList = [], onChange }) => {
    const intl = useIntl();
    const { vessel } = useContext(VesselContext);
    const [crewList, setCrewList] = useState(null);

    useEffect(() => {
        getExtendedCrewListFromVessel(vessel.id).then((response) => {
            setCrewList(response);
        });
    }, [vessel.id]);

    return expanded ? (
        <ItemList
            embedded
            busy={!crewList}
            onItemClick={(crewId) => {
                const isSelected = selectedList.some((selected) => selected.id === crewId);
                if (isSelected) {
                    onChange([...selectedList.filter((selected) => selected.id !== crewId)]);
                } else {
                    onChange([...selectedList, crewList.find((crew) => crew.id === crewId)]);
                }
            }}
        >
            {crewList
                ?.filter((crew) => !excludeUserIdList.includes(crew.user.id))
                .map((crew) => {
                    const isSelected = selectedList.some((selected) => selected.id === crew.id);
                    return crew ? (
                        <ItemList.Item
                            key={crew.id}
                            value={crew.id}
                            prepend={
                                <Image
                                    icon="user"
                                    iconColor={isSelected ? "primary" : null}
                                    grayscale={!isSelected}
                                    rounded
                                    src={crew.user?.picture?.url}
                                />
                            }
                            append={
                                <Checkbox
                                    noMargin
                                    checked={selectedList.some((selected) => selected.id === crew.id)}
                                    onToggle={(checked) => {
                                        if (!checked) {
                                            onChange([...selectedList, crew]);
                                        } else {
                                            onChange([...selectedList.filter((selected) => selected.id !== crew.id)]);
                                        }
                                    }}
                                />
                            }
                        >
                            <TitleDescriptionText
                                title={<Text color={!isSelected ? "base60" : null}>{crew.user?.fullName}</Text>}
                                description={<Word data={crew.role?.name} />}
                            />
                        </ItemList.Item>
                    ) : null;
                })}
        </ItemList>
    ) : (
        <Row>
            <Col span={24}>
                <UserSelect
                    busy={!crewList}
                    userList={crewList
                        ?.filter(
                            (crew) =>
                                !selectedList.some((selected) => selected.id === crew.id) &&
                                !excludeUserIdList.includes(crew.user.id)
                        )
                        .map((crew) => ({
                            ...crew.user,
                            role: wordUtils.getValue(intl.locale, crew.role?.name)
                        }))}
                    onChange={(userId) => {
                        const crew = crewList.find((crew) => crew.user.id === userId);
                        if (selectedList.some((selected) => selected.id === crew.id)) {
                            onChange([...selectedList.filter((selected) => selected.id !== crew.id)]);
                        } else if (crew != null) {
                            onChange([...selectedList, crew]);
                        }
                    }}
                    placeholder={<FormattedMessage id={`${msgKey}.selectCrewMembers`} />}
                />
            </Col>
            {selectedList.length > 0 && (
                <Col span={24}>
                    <ItemList>
                        {selectedList.map((crew) => {
                            return crew ? (
                                <ItemList.Item
                                    key={crew.id}
                                    value={crew.id}
                                    prepend={<Image icon="user" rounded src={crew.user?.picture?.url} />}
                                    append={
                                        <Button
                                            square
                                            size="small"
                                            icon="close"
                                            color="silentError"
                                            onClick={() => {
                                                onChange([...selectedList.filter((c) => c.id !== crew.id)]);
                                            }}
                                        />
                                    }
                                >
                                    <TitleDescriptionText
                                        title={crew.user?.fullName}
                                        description={<Word data={crew.role?.name} />}
                                    />
                                </ItemList.Item>
                            ) : null;
                        })}
                    </ItemList>
                </Col>
            )}
        </Row>
    );
};
